import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Filter, X, Check, ChevronDown, Gauge, Package } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useSafeMode } from '../../contexts/SafeModeContext';
import TagBadge from './TagBadge';

/**
 * TagFilter - Multi-select dropdown for filtering by tags
 * 
 * Supports both built-in filters (effort, release) and dynamic tags.
 * Built-in filters are identified by tag_id starting with '__' and have 
 * an 'isBuiltin' property.
 * 
 * @param {array} availableTags - All tags available for filtering (including built-in)
 * @param {array} selectedTagIds - Currently selected tag IDs
 * @param {function} onFilterChange - Callback with updated tag IDs
 * @param {string} className - Additional classes
 */
const TagFilter = ({
  availableTags = [],
  selectedTagIds = [],
  onFilterChange,
  className
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Separate built-in filters from dynamic tags
  const { builtinTags, dynamicTags } = useMemo(() => {
    const builtin = availableTags.filter(t => t.isBuiltin);
    const dynamic = availableTags.filter(t => !t.isBuiltin);
    return { builtinTags: builtin, dynamicTags: dynamic };
  }, [availableTags]);

  // Group built-in tags by type
  const { effortTags, releaseTags } = useMemo(() => {
    return {
      effortTags: builtinTags.filter(t => t.filterType === 'effort'),
      releaseTags: builtinTags.filter(t => t.filterType === 'release')
    };
  }, [builtinTags]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleTag = (tagId) => {
    if (selectedTagIds.includes(tagId)) {
      onFilterChange(selectedTagIds.filter(id => id !== tagId));
    } else {
      onFilterChange([...selectedTagIds, tagId]);
    }
  };

  const clearAll = () => {
    onFilterChange([]);
    setIsOpen(false);
  };

  const hasSelection = selectedTagIds.length > 0;

  // Render a filter item
  const renderFilterItem = (tag, icon = null) => {
    const isSelected = selectedTagIds.includes(tag.tag_id);
    return (
      <button
        key={tag.tag_id}
        type="button"
        onClick={() => toggleTag(tag.tag_id)}
        className={cn(
          "w-full px-3 py-2 flex items-center gap-2 text-left text-sm transition-colors",
          isSelected ? colors.bgTertiary : "hover:bg-gray-50 dark:hover:bg-gray-800"
        )}
      >
        {icon ? (
          icon
        ) : (
          <span
            className="w-3 h-3 rounded-full flex-shrink-0"
            style={{ backgroundColor: tag.color }}
          />
        )}
        <span className={cn("flex-1", colors.textPrimary)}>{tag.name}</span>
        {tag.usage_count !== undefined && (
          <span className={cn("text-xs", colors.textMuted)}>
            {tag.usage_count}
          </span>
        )}
        {isSelected && (
          <Check className="w-4 h-4 text-indigo-600" />
        )}
      </button>
    );
  };

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors",
          colors.bgInput,
          colors.borderPrimary,
          hasSelection ? "text-indigo-600 dark:text-indigo-400 border-indigo-300 dark:border-indigo-600" : colors.textSecondary,
          "hover:border-gray-400 dark:hover:border-gray-500"
        )}
      >
        <Filter className="w-4 h-4" />
        <span>
          {hasSelection ? `${selectedTagIds.length} filter${selectedTagIds.length > 1 ? 's' : ''}` : 'Filter'}
        </span>
        <ChevronDown className={cn("w-4 h-4 transition-transform", isOpen && "rotate-180")} />
      </button>
      
      {/* Dropdown */}
      {isOpen && (
        <div className={cn(
          "absolute z-50 w-64 mt-1 rounded-lg border shadow-lg overflow-hidden",
          colors.bgCard,
          colors.borderPrimary
        )}>
          <div className="max-h-[350px] overflow-y-auto">
            {/* Effort Section */}
            {effortTags.length > 0 && (
              <>
                <div className={cn("px-3 py-1.5 text-xs font-medium uppercase tracking-wide border-b", colors.textMuted, colors.borderPrimary, colors.bgTertiary)}>
                  Level of Effort
                </div>
                {effortTags.map(tag => renderFilterItem(tag, <Gauge className={cn("w-3.5 h-3.5", colors.textMuted)} />))}
              </>
            )}
            
            {/* Release Section */}
            {releaseTags.length > 0 && (
              <>
                <div className={cn("px-3 py-1.5 text-xs font-medium uppercase tracking-wide border-b", colors.textMuted, colors.borderPrimary, colors.bgTertiary)}>
                  Release Assignment
                </div>
                {releaseTags.map(tag => renderFilterItem(tag, <Package className={cn("w-3.5 h-3.5", colors.textMuted)} />))}
              </>
            )}
            
            {/* Dynamic Tags Section */}
            {dynamicTags.length > 0 && (
              <>
                <div className={cn("px-3 py-1.5 text-xs font-medium uppercase tracking-wide border-b", colors.textMuted, colors.borderPrimary, colors.bgTertiary)}>
                  Tags
                </div>
                {dynamicTags.map(tag => renderFilterItem(tag))}
              </>
            )}
            
            {availableTags.length === 0 && (
              <div className={cn("p-4 text-center text-sm", colors.textMuted)}>
                No filters available
              </div>
            )}
          </div>
          
          {hasSelection && (
            <div className={cn("border-t p-2", colors.borderPrimary)}>
              <button
                type="button"
                onClick={clearAll}
                className={cn(
                  "w-full px-3 py-1.5 text-sm rounded-md flex items-center justify-center gap-1",
                  "text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                )}
              >
                <X className="w-4 h-4" />
                Clear all filters
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TagFilter;
